import { VoxelAsset, VoxelData } from '../../voxelTypes';
import { PALETTE, getRandomizedColor } from '../../voxelColor';
import { getColors, addBox, addMiniBox, WoodFinish, FabricColor } from './CatalogUtils';

export function getFurnitureCatalog(wood: WoodFinish, fabric: FabricColor): VoxelAsset[] {
  const assets: VoxelAsset[] = [];
  const c = getColors({ woodFinish: wood, fabricColor: fabric });

  // --- 1. HEARTH SET ---
  
  // Settle (High-back wooden bench with fabric cushions)
  (() => {
    const voxels: VoxelData[] = [];
    // Base legs and seat frame
    addBox(voxels, -4, 0, -2, 8, 1, 4, c.woodShadow);
    // Armrests
    addBox(voxels, -4, 1, -2, 1, 3, 4, c.woodPrimary);
    addBox(voxels, 3, 1, -2, 1, 3, 4, c.woodPrimary);
    // High-back paneled wood
    addBox(voxels, -3, 1, -2, 6, 7, 1, c.woodPrimary);
    addBox(voxels, -3, 7, -2, 6, 1, 1, c.woodHighlight); // Crown molding
    // Cozy cushions
    addBox(voxels, -3, 1, -1, 6, 1, 3, c.fabric);
    assets.push({
      id: `hearth_settle_${wood}_${fabric}`,
      name: `Hearth Settle (${wood}/${fabric})`,
      category: 'prop',
      description: 'A cozy, high-backed wooden settle bench with comfortable fabric cushions.',
      voxels,
      dimensions: { x: 8, y: 8, z: 4 }
    });
  })();

  // Side Table
  (() => {
    const voxels: VoxelData[] = [];
    // 4 Legs
    addBox(voxels, -2, 0, -2, 1, 3, 1, c.woodShadow);
    addBox(voxels, 1, 0, -2, 1, 3, 1, c.woodShadow);
    addBox(voxels, -2, 0, 1, 1, 3, 1, c.woodShadow);
    addBox(voxels, 1, 0, 1, 1, 3, 1, c.woodShadow);
    // Tabletop
    addBox(voxels, -2.5, 3, -2.5, 5, 1, 5, c.woodPrimary);
    // Little shelf drawer
    addBox(voxels, -2, 2, -1.5, 4, 1, 3, c.woodShadow);
    // Little brass pull handle
    addMiniBox(voxels, -0.5, 4.5, 3.1, 2, 1, 1, PALETTE.LAMP_GOLD);
    assets.push({
      id: `hearth_sidetable_${wood}`,
      name: `Hearth Side Table (${wood})`,
      category: 'prop',
      description: 'A handsome accent side table with a bottom shelf and brass pull-handle drawer.',
      voxels,
      dimensions: { x: 5, y: 4, z: 5 }
    });
  })();

  // Bookshelf
  (() => {
    const voxels: VoxelData[] = [];
    // Frame outer walls
    addBox(voxels, -3, 0, -1.5, 1, 10, 3, c.woodShadow);
    addBox(voxels, 2, 0, -1.5, 1, 10, 3, c.woodShadow);
    addBox(voxels, -2, 0, -1.5, 4, 1, 3, c.woodShadow); // Bottom
    addBox(voxels, -2.5, 10, -1.5, 5, 1, 3, c.woodHighlight); // Header
    // Shelves
    addBox(voxels, -2, 3, -1.3, 4, 1, 2.6, c.woodPrimary);
    addBox(voxels, -2, 6, -1.3, 4, 1, 2.6, c.woodPrimary);
    // Dynamic books (mini-voxels for high resolution!)
    const bookColors = [PALETTE.FLOWER_RED, PALETTE.FLOWER_BLUE, PALETTE.FLOWER_YELLOW, 0x4a8c2d, 0x734f2d, 0xd9e3f0];
    // Shelf 1 books
    for (let x = -3.5; x <= 2.5; x += 0.5) {
      const bookColor = bookColors[Math.abs(Math.round(x * 4)) % bookColors.length]!;
      const seed = Math.sin(x * 12.3) * 43758.5453;
      const rand = seed - Math.floor(seed);
      const h = 3 + rand * 1.5;
      addMiniBox(voxels, x, 2, -1.6, 1, h, 3, bookColor);
    }
    // Shelf 2 books
    for (let x = -3.5; x <= 1.5; x += 0.5) {
      const bookColor = bookColors[(Math.abs(Math.round(x * 5)) + 2) % bookColors.length]!;
      const seed = Math.sin(x * 37.7) * 43758.5453;
      const rand = seed - Math.floor(seed);
      const h = 3.2 + rand * 1.2;
      addMiniBox(voxels, x, 8, -1.6, 1, h, 3, bookColor);
    }
    assets.push({
      id: `hearth_bookshelf_${wood}`,
      name: `Hearth Bookshelf (${wood})`,
      category: 'prop',
      description: 'A tall wooden bookcase packed with colourful books of various heights.',
      voxels,
      dimensions: { x: 6, y: 11, z: 3 }
    });
  })();

  // Rug
  (() => {
    const voxels: VoxelData[] = [];
    // Woven patterned flat rug (0.2 height using scaleY/scale)
    const baseColor = c.fabric;
    const accentColor = PALETTE.FLOWER_RED;
    const border = c.woodPrimary;
    for (let x = -4; x <= 4; x++) {
      for (let z = -3; z <= 3; z++) {
        const isBorder = Math.abs(x) === 4 || Math.abs(z) === 3;
        const isCenterAccent = Math.abs(x) <= 1 && Math.abs(z) <= 1;
        let color = baseColor;
        if (isBorder) color = border;
        else if (isCenterAccent) color = accentColor;
        voxels.push({ x, y: 0.05, z, color, scale: 1, scaleY: 0.1 });
      }
    }
    assets.push({
      id: `hearth_rug_${fabric}`,
      name: `Hearth Rug (${fabric})`,
      category: 'prop',
      description: 'A flat woven area rug with cozy detailed trim and traditional center diamond.',
      voxels,
      dimensions: { x: 9, y: 1, z: 7 }
    });
  })();

  // Standing Lamp
  (() => {
    const voxels: VoxelData[] = [];
    // Heavy base
    addBox(voxels, -1, 0, -1, 2, 1, 2, c.woodShadow);
    // Slender pole
    for (let y = 1; y <= 7; y++) {
      voxels.push({ x: 0, y, z: 0, color: c.woodPrimary, scale: 0.6 });
    }
    // Decorative brass joint
    voxels.push({ x: 0, y: 7.4, z: 0, color: PALETTE.LAMP_GOLD, scale: 0.8 });
    // Emissive bulb
    voxels.push({ x: 0, y: 8, z: 0, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scale: 0.8 });
    // Soft lampshade around the bulb
    for (let x = -1; x <= 1; x++) {
      for (let z = -1; z <= 1; z++) {
        const isCenter = x === 0 && z === 0;
        if (!isCenter) {
          voxels.push({ x, y: 8, z, color: c.fabric, scale: 0.9 });
          voxels.push({ x, y: 9, z, color: c.fabric, scale: 0.8 });
        }
      }
    }
    assets.push({
      id: `hearth_lamp_${wood}_${fabric}`,
      name: `Hearth Standing Lamp (${wood}/${fabric})`,
      category: 'prop',
      description: 'A tall reading floor-lamp with a glowing emissive gold core and warm fabric lampshade.',
      voxels,
      dimensions: { x: 3, y: 10, z: 3 }
    });
  })();

  // Tea Table
  (() => {
    const voxels: VoxelData[] = [];
    // Pedestal leg
    addBox(voxels, -1, 0, -1, 2, 1, 2, c.woodShadow);
    addBox(voxels, -0.5, 1, -0.5, 1, 2, 1, c.woodPrimary);
    // Round table surface (using cylinder approximation)
    for (let x = -3; x <= 3; x++) {
      for (let z = -3; z <= 3; z++) {
        const dist = Math.hypot(x, z);
        if (dist <= 3.2) {
          voxels.push({ x, y: 3, z, color: c.woodPrimary, scale: 1 });
        }
      }
    }
    // High-fidelity tea set on top (mini-voxels!)
    addMiniBox(voxels, -1.0, 8.0, 0, 2, 2, 2, PALETTE.FLOWER_WHITE); // Teapot body
    addMiniBox(voxels, -2.0, 8.5, 0, 1, 1, 1, PALETTE.LAMP_GOLD); // Spout
    addMiniBox(voxels, 1.0, 8.5, -0.5, 1, 1, 2, PALETTE.WOOD_OAK_DARK); // Handle
    // Teacup
    addMiniBox(voxels, 2.0, 8.0, 2.0, 1, 1, 1, PALETTE.FLOWER_WHITE);
    assets.push({
      id: `hearth_teatable_${wood}`,
      name: `Hearth Tea Table (${wood})`,
      category: 'prop',
      description: 'A polished low circular tea table with a tiny ceramic teapot and teacup on top.',
      voxels,
      dimensions: { x: 7, y: 5, z: 7 }
    });
  })();

  // Cushion (Mini-voxels soft seat)
  (() => {
    const voxels: VoxelData[] = [];
    // 3D puffy organic shape
    for (let x = -3; x <= 3; x += 0.5) {
      for (let z = -3; z <= 3; z += 0.5) {
        const distSq = (x*x)/9 + (z*z)/9;
        if (distSq <= 1) {
          const isCore = distSq < 0.6;
          const h = isCore ? 3 : 2;
          addMiniBox(voxels, x, 0, z, 1, h, 1, isCore ? c.fabric : getRandomizedColor(c.fabric, 0.03));
        }
      }
    }
    assets.push({
      id: `hearth_cushion_${fabric}`,
      name: `Hearth Cushion (${fabric})`,
      category: 'prop',
      description: 'A plush, hand-tufted accent floor cushion using fine-threaded weave.',
      voxels,
      dimensions: { x: 4, y: 2, z: 4 }
    });
  })();

  // Wall Shelf
  (() => {
    const voxels: VoxelData[] = [];
    // Board shelf
    addBox(voxels, -3, 3, -1, 6, 1, 2, c.woodPrimary);
    // Two hanging brackets
    addBox(voxels, -2, 1, -0.8, 1, 2, 1, c.woodShadow);
    addBox(voxels, 1, 1, -0.8, 1, 2, 1, c.woodShadow);
    // Tiny decorative pots (mini-voxels!)
    addMiniBox(voxels, -3.0, 8.0, -1.0, 2, 3, 2, PALETTE.ROOF_RED_CLAY); // Terracotta pot
    addMiniBox(voxels, -2.5, 11.0, -0.5, 1, 1, 1, PALETTE.GRASS_BRIGHT); // tiny plant
    addMiniBox(voxels, 1.0, 8.0, -1.0, 2, 2, 2, PALETTE.STONE_LIGHT); // stone pot
    assets.push({
      id: `hearth_wallshelf_${wood}`,
      name: `Hearth Wall Shelf (${wood})`,
      category: 'prop',
      description: 'Floating bracketed wooden shelf with detailed miniature ceramic flower pots.',
      voxels,
      dimensions: { x: 6, y: 4, z: 2 }
    });
  })();


  // --- 2. COTTAGE SET ---

  // Bed
  (() => {
    const voxels: VoxelData[] = [];
    // Four corner posts & bed frame
    addBox(voxels, -3, 0, -4, 1, 4, 1, c.woodShadow); // Headposts
    addBox(voxels, 2, 0, -4, 1, 4, 1, c.woodShadow);
    addBox(voxels, -3, 0, 3, 1, 2, 1, c.woodShadow); // Footposts
    addBox(voxels, 2, 0, 3, 1, 2, 1, c.woodShadow);
    // Headboard slat
    addBox(voxels, -2, 2, -4, 4, 2, 1, c.woodPrimary);
    // Footboard slat
    addBox(voxels, -2, 1, 3, 4, 1, 1, c.woodPrimary);
    // Siderails & mattress base
    addBox(voxels, -3, 1, -3, 6, 1, 6, c.woodPrimary);
    // Soft colorful quilt mattress
    addBox(voxels, -2, 1.5, -3, 4, 1, 6, c.fabric);
    // Fluffy pillows
    addBox(voxels, -1.5, 2.2, -3, 3, 1, 2, PALETTE.FLOWER_WHITE);
    assets.push({
      id: `cottage_bed_${wood}_${fabric}`,
      name: `Cottage Bed (${wood}/${fabric})`,
      category: 'prop',
      description: 'A robust rustic timber bed with a soft quilt, white downy pillow, and carved headboard.',
      voxels,
      dimensions: { x: 6, y: 4, z: 8 }
    });
  })();

  // Wardrobe
  (() => {
    const voxels: VoxelData[] = [];
    // Heavy base and header crown
    addBox(voxels, -3, 0, -2, 6, 1, 4, c.woodShadow);
    addBox(voxels, -3.5, 11, -2.5, 7, 1, 5, c.woodHighlight);
    // Central cabinet body
    addBox(voxels, -3, 1, -2, 6, 10, 4, c.woodPrimary);
    // Carved door frames (slightly protruding at front z = 2.1)
    addBox(voxels, -2.5, 1.5, 2, 2, 9, 1, c.woodShadow); // Left door
    addBox(voxels, 0.5, 1.5, 2, 2, 9, 1, c.woodShadow); // Right door
    // Brass keyhole & lock details (mini-voxels!)
    addMiniBox(voxels, -1.0, 10.0, 6.1, 1, 2, 1, PALETTE.LAMP_GOLD);
    addMiniBox(voxels, 0.0, 10.0, 6.1, 1, 2, 1, PALETTE.LAMP_GOLD);
    assets.push({
      id: `cottage_wardrobe_${wood}`,
      name: `Cottage Wardrobe (${wood})`,
      category: 'prop',
      description: 'A tall double-door armoire wardrobe featuring classic crown molding and brass handle sets.',
      voxels,
      dimensions: { x: 7, y: 12, z: 5 }
    });
  })();

  // Nightstand
  (() => {
    const voxels: VoxelData[] = [];
    // Legs
    addBox(voxels, -1.5, 0, -1.5, 1, 3, 1, c.woodShadow);
    addBox(voxels, 0.5, 0, -1.5, 1, 3, 1, c.woodShadow);
    addBox(voxels, -1.5, 0, 0.5, 1, 3, 1, c.woodShadow);
    addBox(voxels, 0.5, 0, 0.5, 1, 3, 1, c.woodShadow);
    // Slat base shelf
    addBox(voxels, -1, 1, -1, 2, 1, 2, c.woodPrimary);
    // Tabletop
    addBox(voxels, -2, 3, -2, 4, 1, 4, c.woodPrimary);
    // Drawer
    addBox(voxels, -1.5, 2, -1.5, 3, 1, 3, c.woodHighlight);
    // Handle (mini-voxels!)
    addMiniBox(voxels, -0.5, 4.5, 3.1, 1, 1, 1, PALETTE.LAMP_GOLD);
    assets.push({
      id: `cottage_nightstand_${wood}`,
      name: `Cottage Nightstand (${wood})`,
      category: 'prop',
      description: 'A quaint bedside nightstand with a storage shelf and sliding table drawer.',
      voxels,
      dimensions: { x: 4, y: 4, z: 4 }
    });
  })();

  // Dresser
  (() => {
    const voxels: VoxelData[] = [];
    // Solid base cabinet
    addBox(voxels, -4, 0, -2, 8, 5, 4, c.woodShadow);
    // Smooth countertop
    addBox(voxels, -4.5, 5, -2.5, 9, 1, 5, c.woodPrimary);
    // Six drawers (carved highlights)
    addBox(voxels, -3.5, 0.5, 1.6, 3, 1, 1, c.woodHighlight); // Left row
    addBox(voxels, -3.5, 2.0, 1.6, 3, 1, 1, c.woodHighlight);
    addBox(voxels, -3.5, 3.5, 1.6, 3, 1, 1, c.woodHighlight);
    addBox(voxels, 0.5, 0.5, 1.6, 3, 1, 1, c.woodHighlight); // Right row
    addBox(voxels, 0.5, 2.0, 1.6, 3, 1, 1, c.woodHighlight);
    addBox(voxels, 0.5, 3.5, 1.6, 3, 1, 1, c.woodHighlight);
    // Drawers handles (mini-voxels!)
    for (let y = 1; y <= 7; y += 3) {
      addMiniBox(voxels, -4.0, y, 5.1, 1, 1, 1, PALETTE.LAMP_GOLD);
      addMiniBox(voxels, 3.0, y, 5.1, 1, 1, 1, PALETTE.LAMP_GOLD);
    }
    assets.push({
      id: `cottage_dresser_${wood}`,
      name: `Cottage Dresser (${wood})`,
      category: 'prop',
      description: 'A heavy, low chest of six drawers with a wide wooden countertop.',
      voxels,
      dimensions: { x: 9, y: 6, z: 5 }
    });
  })();

  // Quilt Rack
  (() => {
    const voxels: VoxelData[] = [];
    // Ground stabilizing feet
    addBox(voxels, -2.5, 0, -1, 1, 1, 2, c.woodShadow);
    addBox(voxels, 1.5, 0, -1, 1, 1, 2, c.woodShadow);
    // Tall slender frame sides
    for (let y = 1; y <= 6; y++) {
      voxels.push({ x: -2, y, z: 0, color: c.woodPrimary, scale: 0.8 });
      voxels.push({ x: 1.5, y, z: 0, color: c.woodPrimary, scale: 0.8 });
    }
    // High hanging bar
    addBox(voxels, -1.8, 5.8, -0.1, 3.2, 0.4, 0.4, c.woodHighlight as any);
    // Folded gorgeous blanket hanging over the bar (using colorful fabric voxels)
    addBox(voxels, -1.6, 2.5, -0.8, 3.2, 3, 1.6, c.fabric);
    // Blanket fringe/stripes
    addBox(voxels, -1.6, 2, -0.8, 3.2, 0.5, 1.6, PALETTE.FLOWER_RED);
    assets.push({
      id: `cottage_quiltrack_${wood}_${fabric}`,
      name: `Cottage Quilt Rack (${wood}/${fabric})`,
      category: 'prop',
      description: 'An elegant wooden floor rack showcasing a beautifully woven, folded heritage blanket.',
      voxels,
      dimensions: { x: 5, y: 7, z: 2 }
    });
  })();

  // Mirror
  (() => {
    const voxels: VoxelData[] = [];
    // Scroll base feet
    addBox(voxels, -2, 0, -1.5, 4, 1, 3, c.woodShadow);
    // Two side support spindles
    addBox(voxels, -1.8, 1, -0.5, 0.8, 8, 1, c.woodPrimary);
    addBox(voxels, 1, 1, -0.5, 0.8, 8, 1, c.woodPrimary);
    // Mirror Frame (oval shape)
    for (let x = -1.5; x <= 1.5; x += 0.5) {
      for (let y = 2.5; y <= 8.5; y += 0.5) {
        const dx = x / 1.5;
        const dy = (y - 5.5) / 3;
        const dist = dx*dx + dy*dy;
        if (dist <= 1.1) {
          const isRim = dist > 0.75;
          const color = isRim ? c.woodHighlight : PALETTE.GLASS_WINDOW; // Reflective silver glass
          const mat = isRim ? 'opaque' : 'glass';
          voxels.push({ x, y, z: 0, color, scale: 0.5, materialType: mat });
        }
      }
    }
    assets.push({
      id: `cottage_mirror_${wood}`,
      name: `Cottage Mirror (${wood})`,
      category: 'prop',
      description: 'A full-length swivel cheval floor mirror with polished silver glass and rounded timber frame.',
      voxels,
      dimensions: { x: 4, y: 9, z: 3 }
    });
  })();

  // Trunk
  (() => {
    const voxels: VoxelData[] = [];
    // Dome-topped chest body
    addBox(voxels, -3, 0, -2, 6, 3, 4, c.woodPrimary);
    // Curved dome lid (shading on edges)
    addBox(voxels, -3, 3, -1.8, 6, 1, 3.6, c.woodHighlight);
    addBox(voxels, -2.5, 4, -1, 5, 0.8, 2, c.woodHighlight);
    // Metal protective reinforcing bands
    addBox(voxels, -2.8, 0, -2.1, 0.6, 4.5, 4.2, c.metal); // Left band
    addBox(voxels, 2.2, 0, -2.1, 0.6, 4.5, 4.2, c.metal); // Right band
    // Heavy brass key clasp
    addBox(voxels, -0.5, 1.5, 2.1, 1, 1.2, 0.2, PALETTE.LAMP_GOLD);
    assets.push({
      id: `cottage_trunk_${wood}`,
      name: `Cottage Storage Trunk (${wood})`,
      category: 'prop',
      description: 'A heavy keepsake trunk bound in iron straps with an oversized front lockplate.',
      voxels,
      dimensions: { x: 6, y: 5, z: 4.5 }
    });
  })();

  // Reading Chair
  (() => {
    const voxels: VoxelData[] = [];
    // Thick base cushion legs
    addBox(voxels, -2, 0, -2, 4, 1, 4, c.woodShadow);
    // Overstuffed seat cushions
    addBox(voxels, -2, 1, -2, 4, 1.5, 4, c.fabric);
    // Puffy rolled arms
    addBox(voxels, -2.5, 2.5, -2, 1, 2, 4, c.fabric);
    addBox(voxels, 1.5, 2.5, -2, 1, 2, 4, c.fabric);
    // Tall padded armchair back
    addBox(voxels, -2.5, 2.5, -2, 5, 4.5, 1, c.fabric);
    assets.push({
      id: `cottage_readingchair_${fabric}`,
      name: `Cottage Reading Chair (${fabric})`,
      category: 'prop',
      description: 'An exceptionally soft wingback armchair featuring rolled armrests and deep-tufted cushions.',
      voxels,
      dimensions: { x: 5, y: 7, z: 5 }
    });
  })();


  // --- 3. KITCHEN SET ---

  // Cupboard
  (() => {
    const voxels: VoxelData[] = [];
    // Solid base cabinet
    addBox(voxels, -3.5, 0, -2, 7, 4, 4, c.woodShadow);
    // Side columns of upper cabinet
    addBox(voxels, -3.5, 4, -1.8, 1, 7, 3.6, c.woodPrimary);
    addBox(voxels, 2.5, 4, -1.8, 1, 7, 3.6, c.woodPrimary);
    addBox(voxels, -3.5, 11, -2, 7, 1, 4, c.woodHighlight); // Crown top
    // Shelves interior with colorful ceramic plates (mini-voxels!)
    for (let y = 5; y <= 9; y += 2) {
      addBox(voxels, -2.5, y, -1.5, 5, 0.5, 3, c.woodHighlight);
    }
    // Plates stacked (mini-voxels!)
    addMiniBox(voxels, -2.0, 11.0, 0, 3, 3, 1, PALETTE.FLOWER_BLUE); // Blue plates
    addMiniBox(voxels, 2.0, 11.0, 0, 3, 3, 1, PALETTE.FLOWER_YELLOW); // Yellow plates
    // Glazed window frames (glass)
    for (let y = 5; y <= 10; y++) {
      voxels.push({ x: -2.2, y, z: 1.8, color: PALETTE.GLASS_WINDOW, scale: 0.9, materialType: 'glass' });
      voxels.push({ x: 1.2, y, z: 1.8, color: PALETTE.GLASS_WINDOW, scale: 0.9, materialType: 'glass' });
    }
    assets.push({
      id: `kitchen_cupboard_${wood}`,
      name: `Kitchen Cupboard (${wood})`,
      category: 'prop',
      description: 'A tall baker cabinet with glazed glass panel doors showing rows of tableware.',
      voxels,
      dimensions: { x: 7, y: 12, z: 4 }
    });
  })();

  // Prep Table (Butcher block with cutting board)
  (() => {
    const voxels: VoxelData[] = [];
    // Robust heavy-legged columns
    addBox(voxels, -3, 0, -1.5, 1.5, 3, 1.5, c.woodShadow);
    addBox(voxels, 1.5, 0, -1.5, 1.5, 3, 1.5, c.woodShadow);
    addBox(voxels, -3, 0, 0, 1.5, 3, 1.5, c.woodShadow);
    addBox(voxels, 1.5, 0, 0, 1.5, 3, 1.5, c.woodShadow);
    // Slat storage tray underneath
    addBox(voxels, -2, 0.5, -1, 4, 1, 2, c.woodShadow);
    // Heavy butcher block top
    addBox(voxels, -3.5, 3, -2, 7, 1.5, 4, c.woodPrimary);
    // Cutting board & vegetable (mini-voxels!)
    addMiniBox(voxels, -2.0, 9.0, -1.0, 4, 1, 4, PALETTE.WOOD_OAK_LIGHT); // board
    addMiniBox(voxels, -0.5, 10.0, 0, 2, 2, 2, PALETTE.FLOWER_RED); // apple/tomato
    assets.push({
      id: `kitchen_preptable_${wood}`,
      name: `Kitchen Prep Table (${wood})`,
      category: 'prop',
      description: 'A heavy-duty kitchen prep table holding a modular chopping board detail.',
      voxels,
      dimensions: { x: 7, y: 5.5, z: 4 }
    });
  })();

  // Stool
  (() => {
    const voxels: VoxelData[] = [];
    // Three/four splayed legs
    voxels.push({ x: -1, y: 0, z: -1, color: c.woodShadow });
    voxels.push({ x: 1, y: 0, z: -1, color: c.woodShadow });
    voxels.push({ x: -1, y: 0, z: 1, color: c.woodShadow });
    voxels.push({ x: 1, y: 0, z: 1, color: c.woodShadow });
    for (let y = 1; y <= 2; y++) {
      voxels.push({ x: -0.6, y, z: -0.6, color: c.woodPrimary, scale: 0.8 });
      voxels.push({ x: 0.6, y, z: -0.6, color: c.woodPrimary, scale: 0.8 });
      voxels.push({ x: -0.6, y, z: 0.6, color: c.woodPrimary, scale: 0.8 });
      voxels.push({ x: 0.6, y, z: 0.6, color: c.woodPrimary, scale: 0.8 });
    }
    // Round carved seat cap
    addBox(voxels, -1.5, 3, -1.5, 3, 0.8, 3, c.woodHighlight as any);
    assets.push({
      id: `kitchen_stool_${wood}`,
      name: `Kitchen Stool (${wood})`,
      category: 'prop',
      description: 'A simple, sturdy backless barstool with tapered legs and a dished wooden seat.',
      voxels,
      dimensions: { x: 3, y: 4, z: 3 }
    });
  })();

  // Plate Rack
  (() => {
    const voxels: VoxelData[] = [];
    // Backing and frame slats
    addBox(voxels, -3.5, 0, -1, 7, 6, 0.8, c.woodPrimary);
    // Holding rails
    addBox(voxels, -3, 1, 0, 6, 0.8, 0.8, c.woodHighlight as any);
    addBox(voxels, -3, 3.5, 0, 6, 0.8, 0.8, c.woodHighlight as any);
    // Vertical plates in slots (mini-voxels!)
    for (let x = -2.5; x <= 2.5; x += 1.0) {
      const pColor = x % 2 === 0 ? PALETTE.FLOWER_BLUE : PALETTE.FLOWER_WHITE;
      addMiniBox(voxels, x * 2, 3, -1.0, 1, 6, 6, pColor);
    }
    assets.push({
      id: `kitchen_platerack_${wood}`,
      name: `Kitchen Plate Rack (${wood})`,
      category: 'prop',
      description: 'A wall-hanging kitchen plate organizer stocked with colourful clay dinner plates.',
      voxels,
      dimensions: { x: 7, y: 6, z: 2 }
    });
  })();

  // Pantry
  (() => {
    const voxels: VoxelData[] = [];
    // Outer wooden framework
    addBox(voxels, -3, 0, -2, 6, 11, 4, c.woodShadow);
    // Shelves carve-out
    addBox(voxels, -2.5, 1, -1.5, 5, 9, 3, 0x1e1208); // dark shadows interior
    // Shelves horizontal support
    addBox(voxels, -2.5, 3, -1.5, 5, 0.5, 3, c.woodPrimary);
    addBox(voxels, -2.5, 6, -1.5, 5, 0.5, 3, c.woodPrimary);
    // Stocked items inside (mini-voxels!)
    // Jars of preserves, flour bags (mini-voxels!)
    const foodColors = [0xde8c3a, 0xe0c679, 0xb84a39, 0xd0e8da];
    for (let x = -2.0; x <= 1.5; x += 0.8) {
      const col = foodColors[Math.abs(Math.round(x * 10)) % foodColors.length]!;
      addMiniBox(voxels, x * 2, 3, -1, 2, 4, 2, col); // flour sack / jar
    }
    for (let x = -2.0; x <= 1.5; x += 0.8) {
      const col = foodColors[(Math.abs(Math.round(x * 6)) + 1) % foodColors.length]!;
      addMiniBox(voxels, x * 2, 9, -1, 2, 3, 2, col); // preserve jars
    }
    assets.push({
      id: `kitchen_pantry_${wood}`,
      name: `Kitchen Pantry (${wood})`,
      category: 'prop',
      description: 'A rustic wooden cupboard pantry loaded with canvas flour sacks and jars of delicious preserves.',
      voxels,
      dimensions: { x: 6, y: 11, z: 4 }
    });
  })();

  // Washstand
  (() => {
    const voxels: VoxelData[] = [];
    // Small splash panel backboard
    addBox(voxels, -2.5, 3.5, -2, 5, 3, 0.6, c.woodPrimary);
    // Low table cabinet base
    addBox(voxels, -2.5, 0, -2, 5, 3.5, 4, c.woodShadow);
    // Inset basin bowl (white glaze ceramic)
    addBox(voxels, -2, 3.5, -1.5, 4, 0.8, 3, PALETTE.FLOWER_WHITE);
    // Liquid water inside basin
    addBox(voxels, -1.5, 3.8, -1.0, 3, 0.2, 2, PALETTE.WATER_CYAN_DEEP, 'water');
    // Mini metal copper spigot faucet
    addMiniBox(voxels, -0.5, 10, -3.5, 1, 3, 1, PALETTE.LAMP_GOLD);
    addMiniBox(voxels, -0.5, 12, -2.5, 1, 1, 2, PALETTE.LAMP_GOLD);
    assets.push({
      id: `kitchen_washstand_${wood}`,
      name: `Kitchen Washstand (${wood})`,
      category: 'prop',
      description: 'A wood-panelled washstand console carrying an inset ceramic basin bowl and faucet.',
      voxels,
      dimensions: { x: 5, y: 6.5, z: 4 }
    });
  })();

  // Herb Shelf
  (() => {
    const voxels: VoxelData[] = [];
    // Hanging wooden pegboard row
    addBox(voxels, -3, 5, -0.5, 6, 1, 1, c.woodShadow);
    // Dried leafy herb bundles (mini-voxels!)
    const greenColors = [0x3d5a31, 0x4f7331, 0x224a1e, 0x477854];
    for (let x = -2.0; x <= 2.0; x += 1.0) {
      const gCol = greenColors[Math.abs(Math.round(x*4)) % greenColors.length]!;
      // hanging string
      addMiniBox(voxels, x * 2, 9.0, -0.5, 1, 1, 1, PALETTE.THATCH_YELLOW);
      // leafy bunch hanging down
      addMiniBox(voxels, x * 2 - 0.5, 4.0, -1.0, 2, 5, 2, gCol, 'foliage');
    }
    assets.push({
      id: `kitchen_herbshelf_${wood}`,
      name: `Kitchen Herb Shelf (${wood})`,
      category: 'prop',
      description: 'A wall peg rack holding aromatic bundles of fresh leafy green garden herbs hanging to dry.',
      voxels,
      dimensions: { x: 6, y: 6, z: 2 }
    });
  })();

  // Dining Bench
  (() => {
    const voxels: VoxelData[] = [];
    // Bench top plank
    addBox(voxels, -5, 1.5, -1, 10, 0.8, 2, c.woodPrimary);
    // Bench legs
    addBox(voxels, -4, 0, -0.8, 1, 1.5, 1.6, c.woodShadow);
    addBox(voxels, 3, 0, -0.8, 1, 1.5, 1.6, c.woodShadow);
    // Longitudinal support beam
    addBox(voxels, -3, 0.5, -0.4, 6, 0.6, 0.8, c.woodShadow);
    assets.push({
      id: `kitchen_bench_${wood}`,
      name: `Kitchen Dining Bench (${wood})`,
      category: 'prop',
      description: 'A sturdy backless farmhouse dining bench perfectly matching the long tables.',
      voxels,
      dimensions: { x: 10, y: 2.5, z: 2 }
    });
  })();


  // --- 4. WORKSHOP SET ---

  // Writing Desk
  (() => {
    const voxels: VoxelData[] = [];
    // Legs
    addBox(voxels, -3.5, 0, -2, 1, 3, 1, c.woodShadow);
    addBox(voxels, 2.5, 0, -2, 1, 3, 1, c.woodShadow);
    addBox(voxels, -3.5, 0, 1, 1, 3, 1, c.woodShadow);
    addBox(voxels, 2.5, 0, 1, 1, 3, 1, c.woodShadow);
    // Desk drawer housing & top board
    addBox(voxels, -3.5, 3, -2, 7, 1.2, 4, c.woodPrimary);
    // Slanted pigeonholes at back
    addBox(voxels, -3.5, 4.2, -2, 7, 2, 1.5, c.woodHighlight);
    addBox(voxels, -1, 4.2, -0.5, 2, 1, 1.5, 0xfafafa); // Stack of white paper
    // Mini inkwell & quill (mini-voxels!)
    addMiniBox(voxels, 5.0, 8.5, 2, 1.5, 1.5, 1.5, PALETTE.STONE_DARK); // inkwell
    addMiniBox(voxels, 5.0, 10.0, 1, 1, 2.5, 1, PALETTE.FLOWER_WHITE); // feather quill
    assets.push({
      id: `workshop_writingdesk_${wood}`,
      name: `Workshop Writing Desk (${wood})`,
      category: 'prop',
      description: 'An antique wooden writing desk containing slotted pigeonholes, stacked letters, and a tiny quill inkwell.',
      voxels,
      dimensions: { x: 7, y: 6.5, z: 4 }
    });
  })();

  // Tool Bench
  (() => {
    const voxels: VoxelData[] = [];
    // Heavy wood frame legs and side panels
    addBox(voxels, -4, 0, -2.5, 8, 3, 5, c.woodShadow);
    // Underneath drawer
    addBox(voxels, -3, 0.5, -1.8, 6, 1.5, 3.6, c.woodPrimary);
    // Thick tool board backing
    addBox(voxels, -4, 3, -2.5, 8, 5, 0.8, c.woodPrimary);
    // Tools hanging on backboard (mini-voxels!)
    addMiniBox(voxels, -4.0, 10.0, -3.0, 1, 4, 1, PALETTE.FLOWER_RED); // Screwdriver red handle
    addMiniBox(voxels, -4.0, 7.0, -3.0, 1, 3, 1, c.metal); // Screwdriver steel shank
    addMiniBox(voxels, 3.0, 8.0, -3.0, 2, 4, 1, c.metal); // Hanging hand-saw
    // Iron vise on front right corner
    addBox(voxels, 2.8, 3, 1.5, 1, 1.2, 1, c.metal);
    assets.push({
      id: `workshop_toolbench_${wood}`,
      name: `Workshop Tool Bench (${wood})`,
      category: 'prop',
      description: 'A heavy industrial workshop tool bench equipped with a corner steel vise and backboard hand-tools.',
      voxels,
      dimensions: { x: 8, y: 8, z: 5 }
    });
  })();

  // Drafting Stool
  (() => {
    const voxels: VoxelData[] = [];
    // Tall iron/wood legs
    voxels.push({ x: -0.8, y: 0, z: -0.8, color: c.metal });
    voxels.push({ x: 0.8, y: 0, z: -0.8, color: c.metal });
    voxels.push({ x: -0.8, y: 0, z: 0.8, color: c.metal });
    voxels.push({ x: 0.8, y: 0, z: 0.8, color: c.metal });
    for (let y = 1; y <= 4; y++) {
      voxels.push({ x: -0.5, y, z: -0.5, color: c.woodShadow, scale: 0.8 });
      voxels.push({ x: 0.5, y, z: -0.5, color: c.woodShadow, scale: 0.8 });
      voxels.push({ x: -0.5, y, z: 0.5, color: c.woodShadow, scale: 0.8 });
      voxels.push({ x: 0.5, y, z: 0.5, color: c.woodShadow, scale: 0.8 });
    }
    // High dished seat round
    addBox(voxels, -1.2, 4.8, -1.2, 2.4, 0.6, 2.4, c.woodHighlight as any);
    // Mini wooden footrest ring
    addBox(voxels, -1, 1.5, -1, 2, 0.4, 2, c.metal);
    assets.push({
      id: `workshop_draftingstool_${wood}`,
      name: `Workshop Drafting Stool (${wood})`,
      category: 'prop',
      description: 'A tall drafting design stool with iron-braced legs and an adjustable round wood seat.',
      voxels,
      dimensions: { x: 3, y: 5.5, z: 3 }
    });
  })();

  // Supply Shelf
  (() => {
    const voxels: VoxelData[] = [];
    // Open skeletal metal bracket frame
    for (let y = 0; y <= 9; y++) {
      voxels.push({ x: -3, y, z: -1.5, color: c.metal, scale: 0.8 });
      voxels.push({ x: 2.5, y, z: -1.5, color: c.metal, scale: 0.8 });
      voxels.push({ x: -3, y, z: 1.5, color: c.metal, scale: 0.8 });
      voxels.push({ x: 2.5, y, z: 1.5, color: c.metal, scale: 0.8 });
    }
    // Wooden board shelf inserts
    addBox(voxels, -3, 0.5, -1.5, 6, 0.5, 3, c.woodPrimary);
    addBox(voxels, -3, 4.5, -1.5, 6, 0.5, 3, c.woodPrimary);
    addBox(voxels, -3, 8.5, -1.5, 6, 0.5, 3, c.woodPrimary);
    // Colorful supply bottles & gear pieces (mini-voxels!)
    addMiniBox(voxels, -3.0, 2.0, -1.0, 2, 4, 2, PALETTE.GLASS_WINDOW, 'glass'); // Glass beaker
    addMiniBox(voxels, 1.0, 2.0, -1.0, 3, 2, 3, PALETTE.WHEAT_GOLD); // Gear component
    addMiniBox(voxels, -2.0, 10.0, -1.0, 2, 3, 2, PALETTE.FLOWER_RED); // red canister
    assets.push({
      id: `workshop_supplyshelf_${wood}`,
      name: `Workshop Supply Shelf (${wood})`,
      category: 'prop',
      description: 'An open steel framework rack loaded with glass jars, bronze gear templates, and design boxes.',
      voxels,
      dimensions: { x: 6, y: 10, z: 3 }
    });
  })();

  // Display Cabinet
  (() => {
    const voxels: VoxelData[] = [];
    // Tall elegant wood cabinet
    addBox(voxels, -3, 0, -1.5, 6, 1, 3, c.woodShadow);
    addBox(voxels, -3, 10, -1.5, 6, 1, 3, c.woodHighlight);
    addBox(voxels, -3, 1, -1.5, 0.8, 9, 3, c.woodPrimary); // Left pillar
    addBox(voxels, 2.2, 1, -1.5, 0.8, 9, 3, c.woodPrimary); // Right pillar
    // Backing panel
    addBox(voxels, -3, 1, -1.5, 6, 9, 0.8, c.woodShadow);
    // Interior shelves (glass)
    addBox(voxels, -2.2, 4, -1, 4.4, 0.4, 2, PALETTE.GLASS_WINDOW, 'glass');
    addBox(voxels, -2.2, 7, -1, 4.4, 0.4, 2, PALETTE.GLASS_WINDOW, 'glass');
    // Front fully transparent glass window pane
    addBox(voxels, -2.2, 1, 1.3, 4.4, 9, 0.2, PALETTE.GLASS_WINDOW, 'glass');
    // Mini gold trophy model inside (mini-voxels!)
    addMiniBox(voxels, -0.5, 9.0, 0, 1.5, 4, 1.5, PALETTE.LAMP_GOLD, 'emissive');
    assets.push({
      id: `workshop_display_${wood}`,
      name: `Workshop Display Cabinet (${wood})`,
      category: 'prop',
      description: 'A glass museum display showcase holding a central golden artifact lit with warm gold light.',
      voxels,
      dimensions: { x: 6, y: 11, z: 3 }
    });
  })();

  // Craft Table
  (() => {
    const voxels: VoxelData[] = [];
    // Wide utility table
    addBox(voxels, -4, 0, -2, 8, 3, 4, c.woodShadow);
    addBox(voxels, -4.5, 3, -2.5, 9, 0.8, 5, c.woodPrimary);
    // Sewing machine detail (mini-voxels!)
    addMiniBox(voxels, -2.0, 7.6, -1.0, 5, 3, 2, c.metal); // metal sewing base
    addMiniBox(voxels, 1.0, 7.6, -1.0, 1, 5, 2, c.metal); // vertical arm
    addMiniBox(voxels, -1.0, 10.0, -1.0, 3, 1, 2, c.metal); // needle bridge
    addMiniBox(voxels, 2.0, 8.5, -0.5, 1, 2, 1, PALETTE.FLOWER_RED); // colorful thread spool
    assets.push({
      id: `workshop_crafttable_${wood}`,
      name: `Workshop Craft Table (${wood})`,
      category: 'prop',
      description: 'A wide studio workbench featuring a detailed vintage mechanical iron sewing machine on top.',
      voxels,
      dimensions: { x: 9, y: 6, z: 5 }
    });
  })();

  // Map Rack
  (() => {
    const voxels: VoxelData[] = [];
    // Cylindrical wire basket frame
    for (let y = 0; y <= 4; y++) {
      for (let x = -2; x <= 2; x++) {
        for (let z = -2; z <= 2; z++) {
          const isEdge = Math.abs(x) === 2 || Math.abs(z) === 2;
          const isBottom = y === 0;
          if ((isEdge && y > 0) || (isBottom && Math.hypot(x, z) <= 2)) {
            voxels.push({ x, y, z, color: c.woodShadow, scale: 0.9 });
          }
        }
      }
    }
    // Rolled-up map scrolls sticking out at angles (mini-voxels!)
    for (let sy = 3; sy <= 12; sy += 0.8) {
      // left angled scroll
      voxels.push({ x: -0.6 - (sy*0.05), y: sy*0.5, z: 0.2, color: 0xfaf5eb, scale: 0.5 }); // beige paper
      voxels.push({ x: -0.5 - (sy*0.05), y: sy*0.5, z: 0.2, color: PALETTE.WOOD_OAK_DARK, scale: 0.4 }); // wood tie ribbon
      // right angled scroll
      voxels.push({ x: 0.6 + (sy*0.08), y: sy*0.5, z: -0.4, color: 0xfbf9f5, scale: 0.5 });
    }
    assets.push({
      id: `workshop_maprack_${wood}`,
      name: `Workshop Map Rack (${wood})`,
      category: 'prop',
      description: 'A basket-woven chest containing rolled-up parchment world maps and navigation scrolls.',
      voxels,
      dimensions: { x: 5, y: 7, z: 5 }
    });
  })();

  // Plant Stand
  (() => {
    const voxels: VoxelData[] = [];
    // Multi-tier ladder-style stand
    addBox(voxels, -2, 0, -1.5, 4, 1, 3, c.woodShadow); // Tier 1
    addBox(voxels, -1.5, 1, -1.0, 3, 2, 2, c.woodPrimary); // Tier 2
    addBox(voxels, -1, 3, -0.5, 2, 2, 1, c.woodHighlight); // Tier 3
    // Tiny cascading ivy foliage (mini-voxels!)
    addMiniBox(voxels, -3.0, 2.0, 2.0, 2, 2, 2, PALETTE.ROOF_RED_CLAY); // bottom pot
    addMiniBox(voxels, -3.5, 4.0, 2.0, 3, 2, 2, PALETTE.GRASS_BRIGHT, 'foliage'); // plant
    addMiniBox(voxels, -3.5, 1.0, 3.0, 1, 4, 1, PALETTE.GRASS_LUSH, 'foliage'); // trailing ivy vines
    addMiniBox(voxels, -1.0, 10.0, 0, 2, 2, 2, PALETTE.STONE_LIGHT); // top pot
    addMiniBox(voxels, -1.5, 12.0, 0, 3, 3, 2, PALETTE.MOSS, 'foliage'); // top leafy fern
    assets.push({
      id: `workshop_plantstand_${wood}`,
      name: `Workshop Plant Stand (${wood})`,
      category: 'prop',
      description: 'A three-tier step plant pedestal displaying trailing ivy and a lush leafy fern.',
      voxels,
      dimensions: { x: 4, y: 8, z: 4.5 }
    });
  })();


  // --- 5. GARDEN ROOM SET ---

  // Wicker Chair
  (() => {
    const voxels: VoxelData[] = [];
    // Basket-weave wicker legs & bowl base
    addBox(voxels, -2, 0, -2, 4, 1.2, 4, 0xcd853f); // Rattan gold
    // Backrest curves
    addBox(voxels, -2.5, 1.2, -2, 1, 4.5, 4, 0xb87333);
    addBox(voxels, 1.5, 1.2, -2, 1, 4.5, 4, 0xb87333);
    addBox(voxels, -2.5, 1.2, -2, 5, 5, 1, 0xb87333);
    // Comfy white tufted fabric cushion seat
    addBox(voxels, -1.8, 1.2, -1.4, 3.6, 0.8, 3, c.fabric);
    assets.push({
      id: `garden_wickerchair_${fabric}`,
      name: `Garden Wicker Chair (${fabric})`,
      category: 'prop',
      description: 'A rounded basket-weave wicker accent chair with comfortable fabric cushion padding.',
      voxels,
      dimensions: { x: 5, y: 6.5, z: 5 }
    });
  })();

  // Potting Table
  (() => {
    const voxels: VoxelData[] = [];
    // Weathered potting table base
    addBox(voxels, -3.5, 0, -2, 7, 3, 4, c.woodShadow);
    addBox(voxels, -4, 3, -2.2, 8, 0.8, 4.4, c.woodPrimary);
    // Left side soil box
    addBox(voxels, -3, 3.8, -1.5, 3, 1, 3, c.woodHighlight);
    addBox(voxels, -2.5, 4, -1, 2, 0.5, 2, PALETTE.DIRT_DARK); // Dark nutrient soil
    // Terracotta pots & trowel (mini-voxels!)
    addMiniBox(voxels, 2.0, 7.6, -1.0, 3, 3, 3, PALETTE.ROOF_RED_CLAY); // Empty pot
    addMiniBox(voxels, 2.5, 10.6, -0.5, 2, 2, 2, PALETTE.DIRT_LIGHT); // soil in pot
    addMiniBox(voxels, 1.0, 7.6, 1.0, 4, 1, 1, c.metal); // metal spoon spade trowel
    assets.push({
      id: `garden_pottingtable_${wood}`,
      name: `Garden Potting Table (${wood})`,
      category: 'prop',
      description: 'A slatted gardening workbench with a wooden soil bin, hand spade trowel, and terracotta pots.',
      voxels,
      dimensions: { x: 8, y: 5.5, z: 4.5 }
    });
  })();

  // Seed Cabinet
  (() => {
    const voxels: VoxelData[] = [];
    // Small detailed cabinet with many small drawer fronts
    addBox(voxels, -3, 0, -1.5, 6, 8, 3, c.woodShadow);
    addBox(voxels, -3.2, 8, -1.8, 6.4, 0.8, 3.6, c.woodHighlight); // roofcap
    // Drawer panels grid 4x3 (mini-voxels detail!)
    for (let drawX = -5.0; drawX <= 4.0; drawX += 2.5) {
      for (let drawY = 1.0; drawY <= 11.0; drawY += 4.0) {
        addMiniBox(voxels, drawX, drawY, 3, 2, 3, 1, c.woodPrimary); // drawer front
        addMiniBox(voxels, drawX + 0.5, drawY + 1, 3.1, 1, 1, 1, PALETTE.LAMP_GOLD); // drawer cup handle
      }
    }
    assets.push({
      id: `garden_seedcabinet_${wood}`,
      name: `Garden Seed Cabinet (${wood})`,
      category: 'prop',
      description: 'An apothecary seed cabinet with twelve tiny labeled drawers and brass pull-caps.',
      voxels,
      dimensions: { x: 6.5, y: 9, z: 4 }
    });
  })();

  // Fern Stand
  (() => {
    const voxels: VoxelData[] = [];
    // High wooden support stand
    addBox(voxels, -1, 0, -1, 2, 0.8, 2, c.woodShadow); // stand base
    for (let y = 0.8; y <= 5.8; y += 1.0) {
      voxels.push({ x: 0, y, z: 0, color: c.woodPrimary, scale: 0.8 }); // slender central spindle
    }
    addBox(voxels, -1, 5.8, -1, 2, 0.6, 2, c.woodHighlight); // plant plate
    // Giant detailed pot and fern (mini-voxels!)
    addMiniBox(voxels, -1.5, 12.8, -1.5, 3, 3, 3, 0xfbf9f5); // white painted pot
    addMiniBox(voxels, -2.5, 15.8, -2.5, 5, 4, 5, PALETTE.GRASS_LUSH, 'foliage'); // leafy center
    // Drooping palm leaves
    addMiniBox(voxels, -4.5, 14.8, -1.5, 2, 1, 3, PALETTE.GRASS_BRIGHT, 'foliage'); // left frond
    addMiniBox(voxels, 2.5, 14.8, -1.5, 2, 1, 3, PALETTE.GRASS_BRIGHT, 'foliage'); // right frond
    assets.push({
      id: `garden_fernstand_${wood}`,
      name: `Garden Fern Stand (${wood})`,
      category: 'prop',
      description: 'A tall wooden pedestal stand supporting a glazed painted pot filled with lush cascading fern fronds.',
      voxels,
      dimensions: { x: 5, y: 10, z: 5 }
    });
  })();

  // Small Fountain
  (() => {
    const voxels: VoxelData[] = [];
    // Double tiered stone fountain
    // Base bowl
    for (let x = -3; x <= 3; x++) {
      for (let z = -3; z <= 3; z++) {
        const dist = Math.hypot(x, z);
        if (dist <= 3.2 && dist >= 2.0) {
          addBox(voxels, x, 0, z, 1, 1.5, 1, c.stone);
        } else if (dist < 2.0) {
          addBox(voxels, x, 0, z, 1, 0.5, 1, c.stone); // bottom
          addBox(voxels, x, 0.5, z, 1, 0.6, 1, PALETTE.WATER_CYAN_DEEP, 'water'); // water pool
        }
      }
    }
    // Middle column
    addBox(voxels, -1, 1.5, -1, 2, 3, 2, c.stone);
    // Top small bowl
    for (let x = -1.5; x <= 1.5; x += 0.5) {
      for (let z = -1.5; z <= 1.5; z += 0.5) {
        const dist = Math.hypot(x, z);
        if (dist <= 1.5 && dist >= 0.8) {
          voxels.push({ x, y: 4.5, z, color: c.stone, scale: 0.5 });
        } else if (dist < 0.8) {
          voxels.push({ x, y: 4.5, z, color: PALETTE.WATER_CYAN_SHALLOW, scale: 0.5, materialType: 'water' });
        }
      }
    }
    // Cascading water streams down (glass/water material)
    for (let y = 1.0; y <= 4.0; y += 0.5) {
      voxels.push({ x: -1.2, y, z: 0, color: PALETTE.WATER_FOAM, scale: 0.4, materialType: 'water' });
      voxels.push({ x: 1.2, y, z: 0, color: PALETTE.WATER_FOAM, scale: 0.4, materialType: 'water' });
    }
    assets.push({
      id: `garden_fountain_${wood}`,
      name: `Garden Small Fountain`,
      category: 'prop',
      description: 'A double-tiered cobblestone birdbath fountain bubbling with shimmering cyan water cascades.',
      voxels,
      dimensions: { x: 7, y: 5.5, z: 7 }
    });
  })();

  // Bird Table
  (() => {
    const voxels: VoxelData[] = [];
    // Tall slender pole
    addBox(voxels, -1, 0, -1, 2, 0.8, 2, c.woodShadow); // foot base
    for (let y = 0.8; y <= 6.8; y++) {
      voxels.push({ x: 0, y, z: 0, color: c.woodPrimary, scale: 0.8 });
    }
    // Flat platform tray
    addBox(voxels, -2, 6.8, -2, 4, 0.6, 4, c.woodPrimary);
    // Platform rim borders
    addBox(voxels, -2, 7.4, -2, 4, 0.5, 0.5, c.woodHighlight as any);
    addBox(voxels, -2, 7.4, 1.5, 4, 0.5, 0.5, c.woodHighlight as any);
    // Gable peak shingled roof
    for (let rx = -2.5; rx <= 2.5; rx += 0.5) {
      for (let rz = -2.5; rz <= 2.5; rz += 0.5) {
        const distZ = Math.abs(rz);
        const roofY = 10.5 - distZ * 0.7;
        voxels.push({
          x: rx, y: roofY, z: rz,
          color: PALETTE.ROOF_RED_CLAY,
          scale: 0.5
        });
      }
    }
    // Tiny wooden roof truss support pillars
    voxels.push({ x: -1.5, y: 7.8, z: -1.5, color: c.woodShadow, scale: 0.5 });
    voxels.push({ x: 1.5, y: 7.8, z: -1.5, color: c.woodShadow, scale: 0.5 });
    voxels.push({ x: -1.5, y: 7.8, z: 1.5, color: c.woodShadow, scale: 0.5 });
    voxels.push({ x: 1.5, y: 7.8, z: 1.5, color: c.woodShadow, scale: 0.5 });
    assets.push({
      id: `garden_birdtable_${wood}`,
      name: `Garden Bird Table (${wood})`,
      category: 'prop',
      description: 'A miniature bird feeding gazebo with support posts, birdseed shelf, and red shingled gable roof.',
      voxels,
      dimensions: { x: 5, y: 12, z: 5 }
    });
  })();

  // Trellis Shelf
  (() => {
    const voxels: VoxelData[] = [];
    // Shelf planks
    addBox(voxels, -3, 1, -1, 6, 0.8, 2, c.woodPrimary);
    addBox(voxels, -3, 4.5, -1, 6, 0.8, 2, c.woodPrimary);
    // Latice grid backing panel
    for (let x = -3; x <= 3; x++) {
      for (let y = 0; y <= 9; y++) {
        const isLattice = (x + y) % 2 === 0;
        if (isLattice) {
          voxels.push({ x, y, z: -1.2, color: c.woodShadow, scale: 0.8 });
        }
      }
    }
    // Ivy leaves trailing across the lattice (mini-voxels!)
    addMiniBox(voxels, -3.0, 3.0, -1.8, 3, 5, 1, PALETTE.GRASS_BRIGHT, 'foliage');
    addMiniBox(voxels, 1.0, 9.0, -1.8, 4, 6, 1, PALETTE.MOSS, 'foliage');
    // Mini blooming rose highlights on vine
    addMiniBox(voxels, -1.0, 5.0, -2.0, 1, 1, 1, PALETTE.FLOWER_RED);
    addMiniBox(voxels, 2.0, 12.0, -2.0, 1, 1, 1, PALETTE.FLOWER_YELLOW);
    assets.push({
      id: `garden_trellis_${wood}`,
      name: `Garden Trellis Shelf (${wood})`,
      category: 'prop',
      description: 'A tall lattice climbing panel loaded with trailing green ivy vines and sweet wild rosebuds.',
      voxels,
      dimensions: { x: 6, y: 10, z: 3 }
    });
  })();

  // Boot Rack
  (() => {
    const voxels: VoxelData[] = [];
    // Low wood shoe rack frame
    addBox(voxels, -3, 0, -1.5, 6, 0.5, 3, c.woodShadow); // bottom
    addBox(voxels, -3, 2, -1.5, 6, 0.5, 3, c.woodPrimary); // top shelf
    addBox(voxels, -3, 0.5, -1.5, 0.8, 1.5, 3, c.woodShadow); // left wall
    addBox(voxels, 2.2, 0.5, -1.5, 0.8, 1.5, 3, c.woodShadow); // right wall
    // Muddy rainboots detail (mini-voxels!)
    // Boot pair 1 (Yellow)
    addMiniBox(voxels, -2.0, 1.0, -0.5, 1.5, 3.5, 1.5, PALETTE.FLOWER_YELLOW); // left yellow boot shaft
    addMiniBox(voxels, -2.0, 1.0, -1.5, 1.5, 1.2, 1.5, PALETTE.FLOWER_YELLOW); // foot
    addMiniBox(voxels, -2.0, 0.8, -1.5, 1.5, 0.3, 2.5, PALETTE.STONE_DARK); // boot sole
    // Boot pair 2 (Teal)
    addMiniBox(voxels, 1.0, 1.0, -0.5, 1.5, 3.5, 1.5, 0x1d7070); // left teal boot shaft
    addMiniBox(voxels, 1.0, 1.0, -1.5, 1.5, 1.2, 1.5, 0x1d7070); // foot
    addMiniBox(voxels, 1.0, 0.8, -1.5, 1.5, 0.3, 2.5, PALETTE.STONE_DARK); // sole
    assets.push({
      id: `garden_bootrack_${wood}`,
      name: `Garden Boot Rack (${wood})`,
      category: 'prop',
      description: 'A cozy entryway slatted rack holding muddy yellow and forest-teal rubber rainboots.',
      voxels,
      dimensions: { x: 6, y: 3, z: 3 }
    });
  })();


  // --- 6. GATHERING SET ---

  // Long Table
  (() => {
    const voxels: VoxelData[] = [];
    // Thick supportive column legs
    addBox(voxels, -5, 0, -2, 1.5, 3.5, 1.5, c.woodShadow);
    addBox(voxels, 3.5, 0, -2, 1.5, 3.5, 1.5, c.woodShadow);
    addBox(voxels, -5, 0, 0.5, 1.5, 3.5, 1.5, c.woodShadow);
    addBox(voxels, 3.5, 0, 0.5, 1.5, 3.5, 1.5, c.woodShadow);
    // Heavy central strut
    addBox(voxels, -4, 1.2, -0.4, 8, 1, 0.8, c.woodPrimary);
    // Giant thick dining surface
    addBox(voxels, -6, 3.5, -2.5, 12, 1, 5, c.woodPrimary);
    assets.push({
      id: `gathering_longtable_${wood}`,
      name: `Gathering Long Table (${wood})`,
      category: 'prop',
      description: 'A magnificent heavy-set oak banquet dining table designed for village gatherings.',
      voxels,
      dimensions: { x: 12, y: 4.5, z: 5 }
    });
  })();

  // Dining Chair
  (() => {
    const voxels: VoxelData[] = [];
    // Slender legs
    voxels.push({ x: -1, y: 0, z: -1, color: c.woodShadow });
    voxels.push({ x: 1, y: 0, z: -1, color: c.woodShadow });
    voxels.push({ x: -1, y: 0, z: 1, color: c.woodShadow });
    voxels.push({ x: 1, y: 0, z: 1, color: c.woodShadow });
    for (let y = 1; y <= 2.5; y++) {
      voxels.push({ x: -0.8, y, z: -0.8, color: c.woodPrimary, scale: 0.8 });
      voxels.push({ x: 0.8, y, z: -0.8, color: c.woodPrimary, scale: 0.8 });
      voxels.push({ x: -0.8, y, z: 0.8, color: c.woodPrimary, scale: 0.8 });
      voxels.push({ x: 0.8, y, z: 0.8, color: c.woodPrimary, scale: 0.8 });
    }
    // Fabric seat cushion padding
    addBox(voxels, -1.2, 2.5, -1.2, 2.4, 0.6, 2.4, c.fabric);
    // Slat high backrest
    addBox(voxels, -1.2, 3.1, -1.2, 2.4, 4.5, 0.6, c.woodPrimary);
    addBox(voxels, -1.2, 7.6, -1.2, 2.4, 0.6, 0.6, c.woodHighlight as any); // crown cap
    assets.push({
      id: `gathering_chair_${wood}_${fabric}`,
      name: `Gathering Dining Chair (${wood}/${fabric})`,
      category: 'prop',
      description: 'A high-back timber dining chair with structural slat spindles and soft fabric pad.',
      voxels,
      dimensions: { x: 3, y: 8, z: 3 }
    });
  })();

  // Corner Bench
  (() => {
    const voxels: VoxelData[] = [];
    // L-shaped wooden corner bench
    // Segment A (z-axis bench)
    addBox(voxels, -3, 0, -3, 2, 2, 6, c.woodShadow);
    addBox(voxels, -3, 2, -3, 2, 0.6, 6, c.woodPrimary);
    addBox(voxels, -3, 2.6, -3, 0.6, 3, 6, c.woodPrimary); // backrest
    // Segment B (x-axis bench connecting at corner)
    addBox(voxels, -1, 0, 1, 4, 2, 2, c.woodShadow);
    addBox(voxels, -1, 2, 1, 4, 0.6, 2, c.woodPrimary);
    addBox(voxels, -1, 2.6, 2.4, 4, 3, 0.6, c.woodPrimary); // backrest
    assets.push({
      id: `gathering_cornerbench_${wood}`,
      name: `Gathering Corner Bench (${wood})`,
      category: 'prop',
      description: 'A custom L-shaped corner wall-hugging bench with slatted wood frames.',
      voxels,
      dimensions: { x: 7, y: 5.6, z: 7 }
    });
  })();

  // Record Stand
  (() => {
    const voxels: VoxelData[] = [];
    // Small cabinet
    addBox(voxels, -2, 0, -1.5, 4, 4, 3, c.woodShadow);
    addBox(voxels, -2.2, 4, -1.8, 4.4, 0.5, 3.6, c.woodPrimary);
    // Lower shelf slots
    addBox(voxels, -1.5, 1, -1.2, 3, 2.5, 2.4, 0x111111); // dark void cavity
    // Inside record boxes (mini-voxels!)
    for (let rx = -2.0; rx <= 1.0; rx += 1.0) {
      addMiniBox(voxels, rx * 2, 2.0, -1.0, 2, 4, 4, PALETTE.FLOWER_RED);
    }
    // Gramophone on top (mini-voxels!)
    addMiniBox(voxels, -1.5, 9.0, -1.0, 3, 1, 3, c.woodPrimary); // player box
    addMiniBox(voxels, 0, 10.0, 0, 1, 2, 1, PALETTE.LAMP_GOLD); // brass arm
    addMiniBox(voxels, 0, 12.0, -1.5, 3, 3, 3, PALETTE.LAMP_GOLD); // big horn speaker
    assets.push({
      id: `gathering_recordstand_${wood}`,
      name: `Gathering Record Stand (${wood})`,
      category: 'prop',
      description: 'A vintage console table carrying a gold-horn mechanical gramophone player on top.',
      voxels,
      dimensions: { x: 5, y: 7.5, z: 4 }
    });
  })();

  // Game Shelf
  (() => {
    const voxels: VoxelData[] = [];
    // Small square shelf cubbies
    addBox(voxels, -3, 0, -1.5, 6, 6, 3, c.woodShadow);
    // Vertical dividers
    addBox(voxels, -0.4, 0.5, -1.3, 0.8, 5, 2.6, c.woodPrimary);
    addBox(voxels, -2.5, 3, -1.3, 5, 0.6, 2.6, c.woodPrimary);
    // Colorful boxed boardgames inside (mini-voxels!)
    addMiniBox(voxels, -4.0, 2.0, -1.0, 3, 3, 4, PALETTE.FLOWER_RED); // red box
    addMiniBox(voxels, -4.0, 8.0, -1.0, 3, 2, 4, PALETTE.FLOWER_BLUE); // blue box
    addMiniBox(voxels, 1.0, 2.0, -1.0, 3, 3, 4, PALETTE.FLOWER_YELLOW); // yellow box
    assets.push({
      id: `gathering_gameshelf_${wood}`,
      name: `Gathering Game Shelf (${wood})`,
      category: 'prop',
      description: 'A low wooden cabinet filled with colorful boxed boardgames and activity boxes.',
      voxels,
      dimensions: { x: 6, y: 6.5, z: 3 }
    });
  })();

  // Serving Cart
  (() => {
    const voxels: VoxelData[] = [];
    // Two-tier tray shelves
    addBox(voxels, -2, 0.8, -1.5, 4, 0.5, 3, c.woodShadow); // lower shelf
    addBox(voxels, -2, 3.5, -1.5, 4, 0.5, 3, c.woodPrimary); // upper tray
    // Splaying metal struts
    for (let y = 0.5; y <= 3.5; y += 0.5) {
      voxels.push({ x: -1.8, y, z: -1.3, color: c.metal, scale: 0.6 });
      voxels.push({ x: 1.8, y, z: -1.3, color: c.metal, scale: 0.6 });
      voxels.push({ x: -1.8, y, z: 1.3, color: c.metal, scale: 0.6 });
      voxels.push({ x: 1.8, y, z: 1.3, color: c.metal, scale: 0.6 });
    }
    // Curving push handlebar
    addBox(voxels, 1.8, 4.0, -1.3, 0.5, 0.5, 2.6, c.metal as any);
    // Four wood caster wheel hubs on floor
    addBox(voxels, -1.8, 0, -1.3, 0.8, 0.8, 0.8, c.woodShadow);
    addBox(voxels, 1.0, 0, -1.3, 0.8, 0.8, 0.8, c.woodShadow);
    addBox(voxels, -1.8, 0, 0.5, 0.8, 0.8, 0.8, c.woodShadow);
    addBox(voxels, 1.0, 0, 0.5, 0.8, 0.8, 0.8, c.woodShadow);
    // Sparkling mini glass goblets on top (mini-voxels!)
    addMiniBox(voxels, -2.0, 8.0, 0, 1.5, 2.5, 1.5, PALETTE.GLASS_WINDOW, 'glass');
    assets.push({
      id: `gathering_servingcart_${wood}`,
      name: `Gathering Serving Cart (${wood})`,
      category: 'prop',
      description: 'A multi-tier mobile beverage trolley cart with gold handles and tiny glassware.',
      voxels,
      dimensions: { x: 5, y: 5, z: 4.2 }
    });
  })();

  // Floor Cushion (Plush tufted, large scale)
  (() => {
    const voxels: VoxelData[] = [];
    // Big flat circular cushion
    for (let x = -4; x <= 4; x++) {
      for (let z = -4; z <= 4; z++) {
        const distSq = (x*x)/16 + (z*z)/16;
        if (distSq <= 1.0) {
          const h = distSq <= 0.4 ? 1.5 : 0.8;
          voxels.push({
            x, y: 0.1, z,
            color: distSq <= 0.4 ? c.fabric : getRandomizedColor(c.fabric, 0.04),
            scale: 1,
            scaleY: h
          });
        }
      }
    }
    assets.push({
      id: `gathering_floorcushion_${fabric}`,
      name: `Gathering Floor Cushion (${fabric})`,
      category: 'prop',
      description: 'An oversized, low-profile tufted floor seat cushion for circular hearth gatherings.',
      voxels,
      dimensions: { x: 9, y: 2, z: 9 }
    });
  })();

  // Firewood Cradle
  (() => {
    const voxels: VoxelData[] = [];
    // Curved copper metal hoop cradle (U-shape)
    for (let x = -3; x <= 3; x++) {
      const dist = Math.abs(x);
      const y = Math.max(0.2, (dist * dist) * 0.35);
      addBox(voxels, x, y, -2.2, 0.8, 0.8, 4.4, PALETTE.LAMP_GOLD as any); // glowing bronze hoop
    }
    // Support feet bracket
    addBox(voxels, -2, 0, -2, 0.8, 0.6, 4, c.woodShadow);
    addBox(voxels, 1.2, 0, -2, 0.8, 0.6, 4, c.woodShadow);
    // Split textured wood fireplace logs stacked inside
    addBox(voxels, -1.5, 0.8, -1.8, 3, 1, 3.6, PALETTE.WOOD_OAK_DARK); // Log 1
    addBox(voxels, -1.5, 0.8, -1.8, 0.5, 1, 3.6, PALETTE.WOOD_LOG_END); // ring end detail
    addBox(voxels, 1.0, 0.8, -1.8, 0.5, 1, 3.6, PALETTE.WOOD_LOG_END);
    
    addBox(voxels, -1.0, 1.8, -1.8, 2, 1, 3.6, PALETTE.WOOD_OAK_MEDIUM); // Log 2
    addBox(voxels, -1.0, 1.8, -1.8, 0.5, 1, 3.6, PALETTE.WOOD_LOG_END);
    assets.push({
      id: `gathering_firewood_${wood}`,
      name: `Gathering Firewood Cradle`,
      category: 'prop',
      description: 'A warm burnished brass hoop cradle stacking split fireplace logs with circular rings.',
      voxels,
      dimensions: { x: 7, y: 4, z: 5 }
    });
  })();

  return assets;
}
